/* iter-search-visibility · Oct-2026 · "Search Visibility" hub.
 *
 * Job 4 of the Unified Search Visibility roadmap
 * (/app/memory/SEARCH_VISIBILITY_ROADMAP.md): Video GEO Studio +
 * YouTube upload. Generates SEO/GEO-structured video metadata (no
 * subscription needed — just the Emergent LLM key) and, once a
 * Google OAuth client is configured, uploads straight to YouTube.
 * Placeholder cards below point at the still-to-build jobs (Entity
 * Architecture, Brand Signal Monitoring, Programmatic SEO).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Sparkles, Youtube, CheckCircle2, UploadCloud, Clock, FileText, Link2, Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS_STYLE = {
  draft:      { label: 'Draft',      cls: 'bg-secondary text-secondary-foreground' },
  processing: { label: 'Processing', cls: 'bg-amber-100 text-amber-800' },
  published:  { label: 'Published',  cls: 'bg-emerald-100 text-emerald-800' },
  failed:     { label: 'Failed',     cls: 'bg-rose-100 text-rose-800' },
};

function DraftCard({ draft, youtubeConnected, onChanged }) {
  const [title, setTitle] = useState(draft.title);
  const [description, setDescription] = useState(draft.description);
  const [savingMeta, setSavingMeta] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const dirty = title !== draft.title || description !== draft.description;

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.patch(`${API}/video-geo/drafts/${draft.id}`, { title, description }, { withCredentials: true });
      toast.success('Draft updated.');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setSavingMeta(false); }
  };

  const upload = async () => {
    if (!file) { toast.error('Choose a video file first.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('privacy_status', 'private');
      const r = await axios.post(`${API}/video-geo/drafts/${draft.id}/upload`, form, { withCredentials: true });
      toast.success(`Uploaded — processing on YouTube (${r.data.url}).`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setUploading(false); }
  };

  const copySchema = () => {
    try {
      navigator.clipboard?.writeText(JSON.stringify(draft.video_object_schema, null, 2));
      toast.success('VideoObject JSON-LD copied.');
    } catch (_e) {
      toast.error('Could not copy — clipboard access was blocked by the browser.');
    }
  };

  const statusMeta = STATUS_STYLE[draft.status] || STATUS_STYLE.draft;

  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={`video-geo-draft-${draft.id}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Badge className={`${statusMeta.cls} text-[10px]`} data-testid={`video-geo-draft-status-${draft.id}`}>{statusMeta.label}</Badge>
        {draft.youtube_url && (
          <a href={draft.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
            <Link2 className="h-3 w-3" /> {draft.youtube_url}
          </a>
        )}
      </div>

      <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Title (≤100 chars)</label>
      <Input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} data-testid={`video-geo-title-${draft.id}`} />

      <label className="mb-1 mt-2 block text-[10px] uppercase tracking-wider text-muted-foreground">Description</label>
      <Textarea value={description} rows={5} onChange={(e) => setDescription(e.target.value)} className="text-[12px]" data-testid={`video-geo-description-${draft.id}`} />

      <div className="mt-2 flex flex-wrap gap-1">
        {(draft.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
      </div>

      {draft.chapters?.length > 0 && (
        <div className="mt-2 text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">Chapters: </span>
          {draft.chapters.map((c) => `${c.timestamp} ${c.title}`).join(' · ')}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={!dirty || savingMeta} onClick={saveMeta} data-testid={`video-geo-save-${draft.id}`}>
          {savingMeta ? 'Saving…' : 'Save edits'}
        </Button>
        <Button size="sm" variant="ghost" onClick={copySchema} className="gap-1" data-testid={`video-geo-copy-schema-${draft.id}`}>
          <Copy className="h-3 w-3" /> Copy VideoObject schema
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
        <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-[11px]" data-testid={`video-geo-file-${draft.id}`} />
        <Button size="sm" disabled={!youtubeConnected || !file || uploading} onClick={upload} className="gap-1" data-testid={`video-geo-upload-${draft.id}`}>
          <UploadCloud className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : 'Upload to YouTube'}
        </Button>
        {!youtubeConnected && <span className="text-[10.5px] text-muted-foreground">Connect your channel above to enable upload.</span>}
      </div>
    </div>
  );
}

function VideoGeoStudio() {
  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ working_title: '', book_tie_in: '', transcript: '' });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, d] = await Promise.all([
        axios.get(`${API}/youtube/status`, { withCredentials: true }),
        axios.get(`${API}/video-geo/drafts`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setDrafts(d.data.drafts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Video GEO Studio.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    try {
      const r = await axios.get(`${API}/youtube/connect`, { withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start YouTube connect.');
    }
  };

  const generate = async () => {
    if (!form.working_title.trim() || form.transcript.trim().length < 20) {
      toast.error('Working title + at least a short transcript/talking points are required.'); return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API}/video-geo/generate`, form, { withCredentials: true });
      toast.success('SEO/GEO package generated.');
      setForm({ working_title: '', book_tie_in: '', transcript: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-card" data-testid="video-geo-studio">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold">
          <Youtube className="h-3.5 w-3.5" /> Video GEO Studio — SEO/GEO-optimized packages + YouTube upload
        </span>
        <div className="flex items-center gap-2">
          {status?.connected ? (
            <Badge className="gap-1 bg-emerald-100 text-emerald-800 text-[10px]" data-testid="youtube-connected-badge">
              <CheckCircle2 className="h-3 w-3" /> Channel connected
            </Badge>
          ) : (
            <Button size="sm" variant="outline" onClick={connect} disabled={!status?.oauth_configured} className="h-7 gap-1 text-[11px]" data-testid="youtube-connect-btn">
              <Youtube className="h-3.5 w-3.5" /> Connect YouTube channel
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={load} className="h-7 gap-1 text-[11px]" data-testid="video-geo-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {!status?.oauth_configured && (
        <div className="px-3 py-2 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-200" data-testid="youtube-oauth-not-configured">
          YouTube upload isn't connected yet — add <span className="font-mono-stat">GOOGLE_YOUTUBE_CLIENT_ID</span> /
          <span className="font-mono-stat"> GOOGLE_YOUTUBE_CLIENT_SECRET</span> (see SEARCH_VISIBILITY_ROADMAP.md for the
          Google Cloud setup steps). Generating SEO/GEO packages below still works without it.
        </div>
      )}

      <div className="grid gap-2 border-b border-border/60 p-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Working title</label>
          <Input value={form.working_title} onChange={(e) => setForm((f) => ({ ...f, working_title: e.target.value }))}
                  placeholder="e.g. How I outline a 90k-word novel in a weekend" data-testid="video-geo-input-title" />
        </div>
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Book/series tie-in (optional)</label>
          <Input value={form.book_tie_in} onChange={(e) => setForm((f) => ({ ...f, book_tie_in: e.target.value }))}
                  placeholder="e.g. Crown & Rose series" data-testid="video-geo-input-tie-in" />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Transcript or talking points</label>
          <Textarea value={form.transcript} rows={4} onChange={(e) => setForm((f) => ({ ...f, transcript: e.target.value }))}
                      placeholder="Paste the video transcript, or just bullet the key points you cover." className="text-[12px]" data-testid="video-geo-input-transcript" />
        </div>
        <div className="sm:col-span-2">
          <Button onClick={generate} disabled={generating} className="gap-1.5" data-testid="video-geo-generate-btn">
            <Sparkles className="h-3.5 w-3.5" /> {generating ? 'Generating…' : 'Generate SEO/GEO package'}
          </Button>
        </div>
      </div>

      <div className="space-y-2 p-3">
        {busy && drafts.length === 0 && <div className="py-4 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" /></div>}
        {!busy && drafts.length === 0 && (
          <div className="py-4 text-center text-[12px] text-muted-foreground">No drafts yet — generate your first package above.</div>
        )}
        {drafts.map((d) => <DraftCard key={d.id} draft={d} youtubeConnected={!!status?.connected} onChanged={load} />)}
      </div>
    </div>
  );
}

const COMING_NEXT = [
  { title: 'Entity Architecture', desc: 'Canonical brand/entity definition (Wikidata + Google Knowledge Graph) — Job 1.' },
  { title: 'Brand Signal Monitoring', desc: 'Free-tier media mentions + AI-citation tracking — Job 3.' },
  { title: 'Programmatic SEO Pages', desc: 'Batch-generated answer-first pages from the book catalog — Job 2.' },
];

export default function SearchVisibilityPanel() {
  return (
    <div className="mt-4" data-testid="search-visibility-panel">
      <div className="flex items-center gap-1.5 text-[11.5px] font-semibold">
        <FileText className="h-3.5 w-3.5" /> Search Visibility — Unified AI/SEO/GEO system
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        See /app/memory/SEARCH_VISIBILITY_ROADMAP.md for the full job breakdown and sequencing.
      </p>

      <VideoGeoStudio />

      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {COMING_NEXT.map((j) => (
          <div key={j.title} className="rounded-md border border-dashed border-border bg-secondary/20 p-3" data-testid={`search-visibility-coming-next-${j.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <Clock className="h-3 w-3" /> Coming next
            </div>
            <div className="mt-1 text-[12px] font-semibold">{j.title}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{j.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
